// [NEW FILE] PM-9: start an improvement plan (PIP) automatically from the final rating.
//
// The backend has no automatic PIP, so the frontend does it at the moment HR finalises a rating:
// if the rating falls in the lowest N bands of the cycle's rating scheme (N is set under Admin › Scoring & ratings),
// and the employee has no open plan, a PIP DRAFT is created (POST /pips) with the reporting manager as reviewer and
// the employee's weakest KRAs as improvement areas. HR reviews and starts the draft under Improvement plans.
import api from "./api";
import { addDays, utcToday } from "./core";
import { getPerformanceReview } from "./resultApi";
import { getPipBands } from "../utils/ratingScale";

const DURATION_DAYS = 90;

// Which band labels start a PIP: the lowest N bands (config.bands are sorted from highest to lowest).
export const pipLabels = (bands = [], n = getPipBands()) => (n > 0 ? bands.slice(-n).map((b) => b.label) : []);

// The employee's open (draft or active) improvement plan, or null.
export async function findOpenPip(employeeId) {
  const list = await api.get("/pips").then((r) => r.data).catch(() => []);
  return (list || []).find((p) => p.employee_id === employeeId && ["DRAFT", "ACTIVE"].includes(p.status)) || null;
}

// [NEW] Requirement 6: older rating schemes may still have the backend rule switched on, and then the backend starts
// (activates) the plan itself when HR finalises. Such a plan, just created and without any check-in, is turned back into
// a DRAFT: it is cancelled (marked "[replaced]", hidden in the lists) and the frontend creates the draft below.
async function draftInsteadOfBackendPlan(employeeId) {
  const open = await findOpenPip(employeeId);
  if (!open) return null;
  const automatic = /^Automatic PIP trigger/.test(String(open.concern || ""));
  if (open.status !== "ACTIVE" || !automatic) return open;
  const { data: plan } = await api.get(`/pips/${open.pip_plan_id}`);
  if ((plan.reviews || []).length) return open;
  await api.post(`/pips/${open.pip_plan_id}/cancel`, { reason: "[replaced] Automatic plan turned into a draft; HR starts it with two reviewers" });
  return null;
}

export async function startPipIfNeeded(cycleId, employeeId) {
  const { data } = await getPerformanceReview(cycleId, employeeId); // also loads this cycle's PIP rule
  const n = getPipBands();
  const rating = data.performance?.rating;
  const labels = pipLabels(data.config?.bands || [], n);
  if (!n || !rating || !labels.includes(rating)) return { started: false, reason: "rule" };

  if (await draftInsteadOfBackendPlan(employeeId)) return { started: false, reason: "open" };

  const reviewer = data.record?.review_manager_employee_id;
  if (!reviewer) return { started: false, reason: "no-manager" };

  const start = utcToday();
  const end = addDays(start, DURATION_DAYS);
  const weakest = [...(data.kras || [])]
    .sort((a, b) => Number(a.achievement ?? a.score ?? 0) - Number(b.achievement ?? b.score ?? 0))
    .slice(0, 3);
  const areas = weakest.length ? weakest.map((k) => k.name) : ["Overall performance"];
  const objectives = areas.map((name, i) => ({
    objective: `Improve results on ${name}`,
    success_measure: `Agreed target for ${name} met at the review`,
    due_date: addDays(start, Math.round(((i + 1) * DURATION_DAYS) / areas.length)),
  }));
  const { data: pip } = await api.post("/pips", {
    employee_id: employeeId,
    reviewer_employee_id: reviewer,
    concern: `Trigger: Automatic, final rating "${rating}" in ${data.cycle?.name || "this cycle"}\nCreated automatically when HR finalised the rating (rule: lowest ${n} band${n === 1 ? "" : "s"}).`,
    support_offered: "Regular coaching and check-ins with the reporting manager.",
    start_date: start,
    end_date: end,
    next_review_date: addDays(start, 30),
    objectives,
    reason: "Improvement plan drafted automatically from the final rating",
  });
  return { started: true, pipId: pip?.pip_plan_id, rating };
}
